import { useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, MoreHorizontal } from "lucide-react";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@pbz/ui-kit";

import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useGetCountries } from "@/queries/master";
import { useGetAllActiveRoles } from "@/queries/user";
import { LEAD_DETAILS_STATUS, LEAD_STAMP_IDS } from "@/utils/constants";

import ViewLead from "./ViewLead";

export type Lead = {
  leadId: number;
  utmSource: string | null;
  nationality: number | null;
  roleId: number;
  createdOn: string;
  userName: string;
  leadStatusId: number;
  stampId?: number;
};

// Component for nationality cell that uses countries data
const NationalityCell = ({ nationality }: { nationality: number | null }) => {
  const { data: countriesData } = useGetCountries();

  if (!countriesData?.data) {
    return <div>Loading...</div>;
  }

  // Create nationality mapping from countries data
  const nationalityMap: Record<number, string> = {};
  for (const country of countriesData.data) {
    if (country.id && country.nationality) {
      nationalityMap[country.id] = country.nationality;
    }
  }

  return (
    <div>{nationality ? nationalityMap[nationality] || "Other" : "Indian"}</div>
  );
};

// Component for role cell that uses roles data
const RoleCell = ({ roleId }: { roleId: number }) => {
  const { data: rolesData } = useGetAllActiveRoles();

  if (!rolesData?.data) {
    return <div>Loading...</div>;
  }

  // Create role mapping from roles data
  const roleMap: Record<number, string> = {};
  for (const role of rolesData.data) {
    if (role.id && role.name) {
      roleMap[role.id] = role.name;
    }
  }

  return <div>{roleMap[roleId] || "Unknown"}</div>;
};

// Component for actions cell that can use hooks
const ActionsCell = ({ lead }: { lead: Lead }) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isViewLeadOpen, setIsViewLeadOpen] = useState(false);
  const { canUpdateLead, canReadLead } = usePermissionCheck();

  const handleEditLead = () => {
    navigate({ to: `/lead/edit/${lead.leadId}` });
  };

  const handleViewLeadClick = () => {
    setIsViewLeadOpen(true);
    setIsDropdownOpen(false); // Close dropdown when view lead modal opens
  };

  return (
    <>
      <DropdownMenu onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button className="h-8 w-8 p-0" variant="ghost">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canReadLead() && (
            <DropdownMenuItem onClick={handleViewLeadClick}>
              View Lead
            </DropdownMenuItem>
          )}
          {canUpdateLead() && (
            <DropdownMenuItem onClick={handleEditLead}>
              Edit Lead
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ViewLead modal */}
      {isViewLeadOpen && (
        <ViewLead
          leadId={lead.leadId}
          onClose={() => setIsViewLeadOpen(false)}
          onLeadUpdate={() => {
            // Handle lead update if needed
          }}
        />
      )}
    </>
  );
};

export const columns: ColumnDef<Lead>[] = [
  {
    accessorKey: "leadId",
    header: "Lead Id",
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue("leadId")}</div>
    ),
  },
  {
    accessorKey: "createdOn",
    header: ({ column }) => {
      return (
        <Button
          className="h-8 px-2 lg:px-3"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
        >
          Created On
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const date = new Date(row.getValue("createdOn"));
      return <div>{date.toLocaleString()}</div>;
    },
  },
  {
    accessorKey: "utmSource",
    header: "UTM Source",
    cell: ({ row }) => <div>{row.getValue("utmSource") || "-"}</div>,
  },
  {
    accessorKey: "leadStatusId",
    header: "Lead Status",
    cell: ({ row }) => {
      const statusId = row.getValue("leadStatusId") as number;
      const statusMap: Record<number, string> = {
        [LEAD_DETAILS_STATUS.PARTIAL]: "Partial",
        [LEAD_DETAILS_STATUS.COMPLETED]: "Completed",
      };

      return <div>{statusMap[statusId] || "Unknown"}</div>;
    },
  },
  {
    accessorKey: "stampId",
    header: "Stamping Status",
    cell: ({ row }) => {
      const statusId = row.getValue("stampId") as number;
      const statusMap: Record<number, { label: string; color: string }> = {
        [LEAD_STAMP_IDS.LEAD_CREATED]: {
          label: "Lead Created",
          color: "bg-blue-100 text-blue-800",
        },
        [LEAD_STAMP_IDS.BANK_AND_PLAN_SELECTED]: {
          label: "Plan Selected",
          color: "bg-yellow-100 text-yellow-800",
        },
        [LEAD_STAMP_IDS.ARN_UPDATED]: {
          label: "ARN Updated",
          color: "bg-yellow-100 text-yellow-800",
        },
        [LEAD_STAMP_IDS.LEAD_IN_PROGRESS]: {
          label: "Lead Rejected",
          color: "bg-yellow-100 text-yellow-800",
        },
        [LEAD_STAMP_IDS.LEAD_APPROVED]: {
          label: "Partial",
          color: "bg-green-100 text-green-800",
        },
        [LEAD_STAMP_IDS.LEAD_REJECTED]: {
          label: "Partial",
          color: "bg-red-100 text-red-800",
        },
      };
      const status = statusMap[statusId] || {
        label: "Unknown",
        color: "bg-gray-100 text-gray-800",
      };

      return (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}
        >
          {status.label}
        </span>
      );
    },
  },
  {
    accessorKey: "roleId",
    header: "User Role",
    cell: ({ row }) => {
      const roleId = row.getValue("roleId") as number;
      return <RoleCell roleId={roleId} />;
    },
  },
  {
    accessorKey: "nationality",
    header: "Nationality",
    cell: ({ row }) => {
      const nationality = row.getValue("nationality") as number | null;
      return <NationalityCell nationality={nationality} />;
    },
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const lead = row.original;
      return <ActionsCell lead={lead} />;
    },
  },
];
